import { forwardRef, useEffect, useState } from 'react';
import * as R from 'remeda';
import { Box, BoxProps } from '@rossum/ui/material';
import { usePageSpaceContext } from '../../document-canvas-svg/page/PageSpaceContext';
import { useDraggableInteraction } from '../hooks/useDraggableInteraction';
import {
  applyPadding,
  Rectangle2DCoordinates,
  rectangleFromCoordinates,
} from '../utils/geometry';
import { ResizeHandles } from './ResizeHandles';

/** Purely presentational - a general bounding box that can be moved around/resized/interacted with
 * on a page
 * Used in: DatapointBoundingBox, SuggestionBox, SearchResult, SuggestedOperationBox
 */
export type BoundingBoxProps = Omit<BoxProps<'rect'>, 'position' | 'ref'> & {
  cursor?: string | undefined;
  position: Rectangle2DCoordinates;
  color?: string;
  outlineStyle?: 'solid' | 'dashed' | 'none';
  outlineColor?: string;
  canResize?: boolean;
  interactive?: boolean;
  // onMove and onMoveCancel should be unnecessary with new MagicLine
  // it is good API tho
  onMoveStart?: () => void;
  onMove?: (position: Rectangle2DCoordinates) => void;
  onMoveEnd?: (position: Rectangle2DCoordinates) => void;
  onMoveCancel?: () => void;
  onResizeStart?: () => void;
  onResize?: (position: Rectangle2DCoordinates) => void;
  onResizeEnd?: (position: Rectangle2DCoordinates) => void;
  onResizeCancel?: () => void;
  onPositionChanged?: () => void;
};

const BoundingBox = forwardRef<SVGRectElement, BoundingBoxProps>(
  (
    {
      outlineStyle,
      outlineColor,
      position,
      color,
      cursor,
      canResize,
      interactive,
      onMoveStart,
      onMove,
      onMoveEnd,
      onMoveCancel,
      onResizeStart,
      onResize,
      onResizeEnd,
      onResizeCancel,
      onPositionChanged,
      sx,
      ...rectProps
    },
    ref
  ) => {
    const [draftPosition, setDraftPosition] = useState<
      BoundingBoxProps['position'] | null
    >(null);

    const currentPosition = draftPosition ?? position;
    useEffect(() => {
      onPositionChanged?.();
    }, [currentPosition, onPositionChanged]);

    const rectangle = rectangleFromCoordinates(currentPosition);

    const { pageWidth, pageHeight } = usePageSpaceContext();

    const padding = 1;
    const paddedRectangle = applyPadding(rectangle, padding);

    const { handleMouseDown } = useDraggableInteraction({
      onDragStart: () => {
        onMoveStart?.();
        return setDraftPosition(position);
      },
      onDragMove: diff => {
        const x1 = R.clamp(position[0] + diff.x, {
          min: 0,
          max: pageWidth - (position[2] - position[0]),
        });

        const y1 = R.clamp(position[1] + diff.y, {
          min: 0,
          max: pageHeight - (position[3] - position[1]),
        });

        const x2 = R.clamp(position[2] + diff.x, {
          min: position[2] - position[0],
          max: pageWidth,
        });

        const y2 = R.clamp(position[3] + diff.y, {
          min: position[3] - position[1],
          max: pageHeight,
        });

        const r = Math.round;
        const newPos: Rectangle2DCoordinates = [r(x1), r(y1), r(x2), r(y2)];

        onMove?.(newPos);
        setDraftPosition(newPos);
      },
      onDragEnd: diff => {
        if (draftPosition && (Math.abs(diff.x) > 0 || Math.abs(diff.y) > 0)) {
          onMoveEnd?.(draftPosition);
        }
        setDraftPosition(null);
      },
      onDragCancel: () => {
        onMoveCancel?.();
        setDraftPosition(null);
      },
    });

    const strokeWidth = 2;
    const cornerHandleRadius = 3;

    return (
      <>
        <Box
          component="rect"
          {...paddedRectangle}
          vectorEffect="non-scaling-stroke"
          pointerEvents={interactive ? 'all' : 'none'}
          ref={ref}
          strokeWidth={strokeWidth}
          rx={cornerHandleRadius}
          ry={cornerHandleRadius}
          onMouseDownCapture={canResize ? handleMouseDown : undefined}
          data-dragged={draftPosition === null ? undefined : true}
          sx={{
            fillOpacity: 0.25,
            fill: color ?? 'transparent',
            stroke: outlineStyle !== 'none' ? outlineColor : 'transparent',
            strokeDasharray: outlineStyle === 'dashed' ? '4 4' : undefined,
            cursor: cursor ?? 'pointer',
            [`&:hover`]: {
              stroke: theme => theme.palette.primary.dark,
              strokeDasharray: 'none',
            },
            ...sx,
          }}
          {...rectProps}
        />
        {canResize && (
          <svg
            style={{ overflow: 'visible' }}
            viewBox={`0 0 ${paddedRectangle.width} ${paddedRectangle.height}`}
            {...paddedRectangle}
          >
            <ResizeHandles
              strokeWidth={strokeWidth}
              cornerHandleRadius={cornerHandleRadius}
              onResizeStart={onResizeStart}
              onResize={onResize}
              onResizeCancel={onResizeCancel}
              onResizeEnd={onResizeEnd}
              position={position}
              outlineColor={outlineColor}
              draftPosition={draftPosition}
              setDraftPosition={setDraftPosition}
            />
          </svg>
        )}
      </>
    );
  }
);

BoundingBox.displayName = 'BoundingBox';

export { BoundingBox };
